import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SeekAiChatPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Seek Ai Chat"
      date="NOVEMBER 2025"
    >
      {/* 1) Introduction and Scope */}
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how the mobile application “Seek Ai Chat”
        (the “App”) collects, uses, stores, and discloses information about
        users. The Policy applies to all features of the App, including
        onboarding and authentication, the AI chat interface, optional cloud
        sync of chat history, subscriptions and entitlement management, push
        notifications, analytics, and customer support. We design the App so
        most content can remain on your device unless you choose to enable cloud
        features. We do not operate our own backend servers for chat
        processing; AI prompts are sent directly to the model provider over
        HTTPS. By using the App, you acknowledge that you have read and
        understood this Policy and agree to its terms. If you do not agree, you
        should uninstall the App. Supplemental notices inside the App may
        provide more detail about specific features and should be read together
        with this Policy to make informed choices.
      </p>

      {/* 2) Definitions */}
      <div className={s.title}>2) Definitions</div>
      <p>
        For clarity, the following definitions apply. “Personal Data” means any
        information relating to an identified or identifiable natural person,
        including identifiers provided by authentication or subscription
        platforms. “Processing” means operations performed on data such as
        collection, storage, use, transmission, and deletion. “Controller”
        refers to the person determining the purposes and means of processing,
        while “Processors/Service Providers” are third parties that process data
        on our behalf, such as authentication, analytics, notifications, cloud
        sync, subscription management, and AI inference. “Chat Content” includes
        prompts you type, attached materials, and the model’s responses.
        “Technical Data” means device and app information necessary for
        reliability and security, such as app version, language, crash logs in
        aggregated form, and notification tokens. “Pseudonymization” refers to
        processing that reduces direct identifiability without eliminating it
        entirely. These terms aim to remove ambiguity and ensure consistent
        interpretation of the Policy.
      </p>

      {/* 3) Controller and Contact Information */}
      <div className={s.title}>3) Controller and Contact Information</div>
      <p>
        The data controller for the App is Aleksandra Rakitina. You may contact
        us for privacy questions, requests to access, correct, delete, restrict,
        or object to processing of your Personal Data, data portability, or
        incident notifications at{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>. We respond
        within a reasonable time and may request additional information to
        verify your identity, including proof that you control the account or
        device from which the request originates. Communications about privacy
        may be retained for record-keeping and compliance. If your request
        involves platform-level settings such as subscription management or
        notification permissions, we may direct you to the appropriate in-app or
        system settings. For your security, we will not disclose subscription
        details or access credentials to unverified parties and may refuse or
        limit requests where allowed by law, for example when fulfilling a
        request would adversely affect the rights and freedoms of others.
      </p>

      {/* 4) Data Minimization Principles */}
      <div className={s.title}>4) Data Minimization Principles</div>
      <p>
        We apply purpose limitation and data minimization. We collect only what
        is necessary to deliver features you choose to use, and we try to
        process data on the device wherever feasible. Authentication collects
        essential identifiers to create your account and restore purchases.
        Subscriptions rely on entitlements and product identifiers without
        receiving payment card details. Analytics focuses on operational
        quality, aggregated usage, and crash diagnostics rather than unbounded
        profiling. Chat Content is sent to the AI provider only to generate a
        response and is not routed through our own servers. Optional cloud sync
        is off by default; when enabled, only the fields required to synchronize
        chats are uploaded to your account’s cloud storage. Retention periods
        are limited to what is necessary for service, security, and legal
        obligations. Where practical, we use aggregation or pseudonymization to
        reduce identifiability and restrict secondary uses unless compatible
        with the original purpose and supported by appropriate safeguards.
      </p>

      {/* 5) Categories of Data We Process */}
      <div className={s.title}>5) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process the following categories.
        Account and authentication data: email address, account ID, and tokens
        from the platform-level sign-in methods you select (Apple ID, Google, or
        email/password). Chat Content: prompts, responses, timestamps, and
        conversation identifiers. Technical and diagnostic data: device model,
        operating system version, app version, locale and time zone, anonymized
        crash reports, and performance indicators. Subscription and entitlement
        data: product identifiers, trial status, renewal status, cancellation
        date, and related metadata for restoring access. Analytics events:
        screen views, feature usage, funnels, and campaign-free performance
        metrics oriented to stability and product improvement. Notification
        tokens and preferences: information required to deliver service messages
        or tips you opted in to receive. Optional cloud sync data: chat history
        and metadata if you enable synchronization. We do not collect contacts
        or photos unless a future feature explicitly asks and you grant system
        permission.
      </p>

      {/* 6) Sources of Data and Voluntary Provision */}
      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        The primary source of data is you, through registration, chat input, and
        settings. Additional data arises automatically from your use of the App,
        such as event logs and crash diagnostics that help us maintain
        reliability. If you enable optional cloud sync, your chat history is
        uploaded to an account-linked cloud database so you can access it across
        devices. Providing certain data is voluntary, but some features will not
        function without it: authentication is required for account-linked
        features and restoring subscriptions; notification tokens are required
        to deliver push messages; analytics improve stability and allow us to
        resolve defects. You can disable sync at any time. Local data remains on
        your device until you delete it in the App or uninstall the App. When
        you withdraw a permission or disable a feature, new data stops flowing
        to that component, though previously stored data may persist as required
        for lawful purposes, operational security, or verified deletion in the
        relevant system.
      </p>

      {/* 7) Purposes and Legal Bases */}
      <div className={s.title}>
        7) Purposes and Legal Bases (Contract, Legitimate Interests, Consent)
      </div>
      <p>
        We process data for several clearly defined purposes. Contract: to
        provide core functionality you requested, including onboarding,
        authentication, AI chat, and restoring purchases; without these, the App
        cannot fulfill its basic promise. Legitimate interests: to secure and
        stabilize the App, to detect abuse, prevent fraud, and improve
        performance and usability, while balancing these interests against your
        rights and expectations through minimization and controls. Consent: for
        optional features such as certain notifications or cloud synchronization
        where law or platform policy requires opt-in; you can withdraw consent
        in settings without affecting the lawfulness of prior processing. Legal
        obligations: to comply with applicable laws, handle disputes, respond to
        lawful requests, and maintain necessary records. Where a purpose
        meaningfully changes, we will update this Policy and, if required, seek
        additional consent. We do not sell Personal Data, and we do not use Chat
        Content for unrelated advertising or marketing.
      </p>

      {/* 8) AI Chat Processing and Content Handling */}
      <div className={s.title}>8) AI Chat Processing and Content Handling</div>
      <p>
        When you send a message, the App transmits your prompt to an AI model
        provider using HTTPS. We do not route Chat Content through our own
        servers. The provider may temporarily process the content to generate
        the response, ensure security, or operate the service. We transmit only
        the data needed to fulfill your request, such as the prompt, formatting
        parameters like Markdown support, and technical metadata necessary for
        routing and quota enforcement. On your device, chat history is stored
        locally in a database (SQLite or Hive). If you enable optional sync, the
        App uploads conversations and metadata to a cloud database associated
        with your authenticated account so you can access them on multiple
        devices. You can clear local history in settings, and you may request
        deletion of synced data subject to identity verification and technical
        constraints of the cloud provider. Do not submit sensitive information
        you are not comfortable sharing with an AI system.
      </p>

      {/* 9) Local Storage and Optional Cloud Sync */}
      <div className={s.title}>9) Local Storage and Optional Cloud Sync</div>
      <p>
        By default, the App stores your conversations locally so you can keep
        using the core features without sending Chat Content to our
        infrastructure. Local storage includes message bodies, timestamps, and
        conversation IDs. If you turn on cloud sync, your chat history and
        related metadata are transmitted to a managed database associated with
        your account. Access to cloud data is protected by authentication tokens
        and access controls provided by the platform. Disabling sync stops new
        uploads; local storage continues. Deleting the App removes local data
        but does not automatically remove cloud data you previously uploaded. To
        request removal of cloud records, contact us at the address above; after
        we verify your identity, we will coordinate deletion within a reasonable
        timeframe, subject to the provider’s backup and retention schedules.
        Keep in mind that intermittent connectivity may delay synchronization or
        deletion until your device is online, and some deletions may take time
        to reflect across replicas.
      </p>

      {/* 10) Authentication and Account Management */}
      <div className={s.title}>10) Authentication and Account Management</div>
      <p>
        The App supports platform sign-in (Apple, Google) and email-based
        authentication. From your chosen method, we receive a unique account
        identifier, your email address, and, where available, your display name.
        We use this information to create an account, link entitlements, and
        enable multi-device access when you opt in. We do not store passwords in
        plain text and rely on the authentication provider to validate
        credentials securely. You can sign out in the App at any time, which
        disables cloud features on that device until you sign in again. To
        request account deletion, email us from the address associated with your
        account. After verifying your identity, we will delete account-linked
        data under our control, except where retention is required by law or
        necessary to resolve disputes, prevent fraud, or maintain accurate
        financial records. If deletion affects active subscriptions, your access
        may end before the end of the paid period depending on platform rules.
      </p>

      {/* 11) Subscriptions, Billing, and Entitlements */}
      <div className={s.title}>11) Subscriptions, Billing, and Entitlements</div>
      <p>
        The App offers subscriptions managed through platform billing with a
        trial period of 3 days and plans currently priced at $6.99 per week or
        $24.99 per month. We rely on an entitlement system to determine your
        access level, such as unlimited messages and full chat history for paid
        tiers. We receive product identifiers, trial eligibility, renewal
        status, expiration, and cancellation dates to grant or revoke access. We
        do not receive or store payment card numbers; those are handled by the
        platform. You can manage or cancel your subscription in your device
        account settings. We retain certain transactional metadata for fraud
        prevention, troubleshooting, and accounting. If an issue interrupts
        renewal, the App may offer a grace period as supported by the platform,
        and access may be restored automatically upon successful payment.
        Refunds, if any, are governed by the platform’s policies. Price changes
        or plan updates will be communicated according to platform rules and
        applicable law.
      </p>

      {/* 12) Push Notifications and In-App Messages */}
      <div className={s.title}>12) Push Notifications and In-App Messages</div>
      <p>
        With your permission, the App may send push notifications about service
        status, feature tips, and reminders, for example “Your AI is ready for a
        new question.” Notification delivery requires a device token issued by
        the platform, which we and our providers use solely to route messages to
        your device. You can manage or revoke notification permissions in your
        device settings and, when provided, in the App’s notification
        preferences. Some messages are service-critical, such as security
        notices or subscription issues, and may be delivered as permitted by law
        even if marketing notifications are disabled. We encourage you to review
        your settings to match your preferences. Notification content is concise
        and does not include sensitive Chat Content. To avoid unintended
        disclosure on shared devices, adjust lock-screen preview settings. If
        you uninstall the App, the platform may invalidate your token
        automatically, though intermittent delays can occur before the token is
        fully deactivated across systems.
      </p>

      {/* 13) Analytics, Performance, and Crash Diagnostics */}
      <div className={s.title}>
        13) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use analytics to understand feature reliability, improve performance,
        and fix crashes. Typical events include screen views, onboarding
        completion, paywall impressions, trial start, purchase success or
        failure, renewal, cancellation, and feature usage counts. Metrics are
        oriented to product quality rather than behavioral profiling. Where
        supported, data is aggregated or pseudonymized, and we avoid collecting
        content from your chats for analytics purposes. Crash diagnostics help us
        identify device-specific issues, OS incompatibilities, and regressions
        after updates. You can limit analytics by adjusting system-level privacy
        settings where available, but doing so may reduce our ability to
        troubleshoot issues affecting your experience. We do not combine
        analytics with third-party advertising networks for targeted ads.
        Analytics data may be retained for a period proportionate to monitoring
        product stability trends and thereafter either deleted or further
        aggregated so individuals are not reasonably identifiable.
      </p>

      {/* 14) Data Sharing with Service Providers */}
      <div className={s.title}>14) Data Sharing with Service Providers</div>
      <p>
        To operate the App, we share limited data with vetted service providers
        acting on our instructions. Examples include authentication providers,
        cloud databases for optional sync, entitlement and subscription
        platforms, push notification services, crash reporting, and the AI model
        provider processing your prompts. Providers are bound by terms that
        prohibit using Personal Data for their independent marketing and require
        appropriate security. We do not sell Personal Data, and we do not share
        Chat Content with social networks or advertisers. If we are legally
        required to disclose data to competent authorities, we will review the
        request for validity and scope. In the event of a corporate transaction
        affecting the App, successor entities will be bound by this Policy or
        will notify you about material changes so you can make informed choices.
        We do not publicly disclose individual user data, and we aggregate
        statistics whenever feasible before publication.
      </p>

      {/* 15) Security Measures */}
      <div className={s.title}>15) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational measures
        designed to protect Personal Data against unauthorized access,
        alteration, disclosure, or destruction. Examples include encryption in
        transit via HTTPS, platform-level encryption at rest for cloud
        databases, restricted access based on least privilege, and token-based
        authentication. We regularly review dependencies and apply updates to
        address known vulnerabilities within reasonable release cycles. While no
        method of transmission or storage is perfectly secure, we work to reduce
        exposure by minimizing the data we collect and by isolating content
        on-device by default. If we become aware of a security incident
        affecting Personal Data, we will investigate promptly, take appropriate
        remediation steps, and notify affected users and authorities when
        required by law. You can help by keeping your device software up to
        date, using strong device passcodes, and avoiding sharing account
        credentials with others.
      </p>

      {/* 16) Data Retention and Deletion */}
      <div className={s.title}>16) Data Retention and Deletion</div>
      <p>
        We retain Personal Data only as long as needed for the purposes
        described in this Policy, including providing the service, maintaining
        security, complying with legal obligations, and resolving disputes.
        Local chat history remains on your device until you delete it in the App
        or uninstall the App. Synced chat data, if enabled, is retained in your
        account’s cloud database until you request deletion or clear it in the
        App, subject to the provider’s backup cycles. Subscription and
        entitlement records may be kept for a longer period to support fraud
        prevention and accounting requirements, typically in pseudonymized or
        aggregated form. Crash and performance logs are retained for trend
        analysis and then deleted or anonymized. When deletion requests are
        verified, we aim to complete them within a reasonable period,
        acknowledging that technical constraints such as offline devices and
        distributed backups can introduce delays before all replicas are purged.
      </p>

      {/* 17) International Transfers */}
      <div className={s.title}>17) International Transfers</div>
      <p>
        Depending on your location and the location of our service providers,
        your data may be processed in countries that may have data protection
        rules different from those in your jurisdiction. Where required, we rely
        on appropriate safeguards for cross-border transfers, such as
        contractual protections and platform-level mechanisms. Regardless of
        where processing occurs, we require service providers to implement
        security measures consistent with this Policy and applicable law. You
        may contact us for more information about the relevant transfer
        mechanisms applicable to your data and, where appropriate, copies of
        relevant contractual commitments that do not reveal confidential
        commercial information. By using the App, you understand that data may
        be processed in multiple jurisdictions to deliver the service reliably
        and efficiently.
      </p>

      {/* 18) Your Rights and Choices */}
      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, update, or
        delete your Personal Data; to restrict or object to certain processing;
        and to data portability. You may also withdraw consent where processing
        is based on consent. We provide in-app controls for sign-out, clearing
        local history, and disabling notifications. To exercise other rights,
        contact us at{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a> from the
        email associated with your account, and we will respond after verifying
        your identity. If we cannot fulfill a request for legal or technical
        reasons, we will explain the reason unless restricted by law. You also
        have the right to lodge a complaint with a supervisory authority.
        Exercising your rights is free of charge, except for excessive or
        unfounded requests where permitted by law.
      </p>

      {/* 19) Children’s Privacy */}
      <div className={s.title}>19) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by applicable law. If you believe a child has provided Personal
        Data without proper authorization, please contact us at{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>, and we
        will take steps to remove the information and terminate the account if
        applicable. Parents or guardians who discover unauthorized use may use
        device-level controls to prevent access. If we learn that we
        inadvertently processed data from a minor without appropriate consent,
        we will promptly delete such data. Age-related features, if later
        introduced, will include appropriate consent mechanisms consistent with
        platform policies and legal requirements.
      </p>

      {/* 20) Do Not Track, Cookies, and SDK Disclosures */}
      <div className={s.title}>
        20) Do Not Track, Cookies, and SDK Disclosures
      </div>
      <p>
        The App does not use traditional browser cookies because it is a mobile
        application; however, it uses software development kits (SDKs) that may
        store identifiers to operate features such as authentication,
        notifications, analytics, and entitlements. These identifiers function
        similarly to cookies by enabling session continuity or measurement.
        Mobile operating systems may offer privacy controls such as limiting ad
        tracking or resetting device identifiers; the App does not rely on
        advertising identifiers for targeted advertising. Do Not Track signals
        are a browser technology and generally do not apply to native apps;
        nonetheless, we avoid cross-app tracking for marketing. You can manage
        SDK-related permissions via system settings, and we keep SDK usage
        limited to the purpose necessary for each feature.
      </p>

      {/* 21) Third-Party Links and External Content */}
      <div className={s.title}>21) Third-Party Links and External Content</div>
      <p>
        The App may display links to external websites, documentation, or
        support resources. When you follow a link, you leave the App environment
        and are subject to the privacy practices of the destination. We do not
        control third-party content and are not responsible for their policies.
        If a link requests additional permissions or personal information,
        review those requests carefully before proceeding. For your safety, do
        not reuse passwords and avoid sharing sensitive Chat Content through
        external forms unless you trust the recipient. If a third-party resource
        becomes unavailable or changes its terms, the App’s link may be removed
        or updated in a subsequent release without notice to maintain user
        safety and consistency with this Policy.
      </p>

      {/* 22) Changes to This Policy */}
      <div className={s.title}>22) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will be adjusted when changes are
        made. Material changes that affect your rights or how we use Personal
        Data will be highlighted in-app, and where legally required, we will
        request your consent for new uses. Continued use of the App after the
        effective date signifies acceptance of the updated Policy. We encourage
        you to review the Policy periodically, especially when enabling new
        features such as cloud sync or additional notification categories.
        Archived versions may be available upon request for comparison. If you
        disagree with changes, you may stop using the App and request deletion
        of your account and associated cloud data.
      </p>

      {/* 23) Feature-Specific Disclosures */}
      <div className={s.title}>
        23) Feature-Specific Disclosures (AI, Sync, Paywall)
      </div>
      <p>
        AI Chat: prompts and responses are processed by the AI provider solely
        to generate replies and maintain service security. Avoid sharing
        sensitive personal, financial, or medical information in prompts. Sync:
        disabled by default; when enabled, chat history is transmitted to your
        account’s cloud database, accessible across your devices after sign-in.
        Paywall and Subscriptions: entitlement checks are used to determine
        access; we do not store payment card numbers. Trials, renewals, and
        cancellations are controlled by the platform, and certain grace periods
        may apply. If a payment fails, access may be temporarily reduced until
        restoration. These feature-level disclosures complement the broader
        sections above and are intended to set clear expectations at the point
        of use.
      </p>

      {/* 24) Data Subject Requests: Process and Verification */}
      <div className={s.title}>
        24) Data Subject Requests: Process and Verification
      </div>
      <p>
        To submit a privacy request, email{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a> using the
        address linked to your account and include a concise description of the
        request (access, correction, deletion, restriction, objection,
        portability). We will verify your identity by checking account ownership
        and may request limited additional information to prevent unauthorized
        access. If you authorize an agent, we may request proof of
        authorization and your verification. We will respond within a reasonable
        time, providing the requested information or an explanation if we cannot
        comply in whole or in part. Where feasible, we will deliver data in a
        portable and machine-readable format. We will not discriminate against
        you for exercising privacy rights, though some features may be
        unavailable if necessary data is deleted.
      </p>

      {/* 25) Legal Bases for EEA/UK and Disclosures for California */}
      <div className={s.title}>
        25) Legal Bases for EEA/UK and Disclosures for California
      </div>
      <p>
        For users in the EEA/UK, our legal bases include performance of a
        contract (core functionality), legitimate interests (security, stability,
        product improvement), consent (optional notifications and sync), and
        legal obligations (records and compliance). You may lodge a complaint
        with your local supervisory authority. For California residents, we do
        not “sell” or “share” personal information as defined by law, nor do we
        use or disclose sensitive personal information for purposes other than
        those permitted. You may request access, deletion, and correction, and
        you have the right to limit certain uses. We will not retaliate for
        exercising your rights. Metrics about requests may be reported in
        aggregated form where required by law and will not include Chat Content.
      </p>

      {/* 26) Security of Communications and Storage */}
      <div className={s.title}>
        26) Security of Communications and Storage
      </div>
      <p>
        All communications with service providers occur over HTTPS, and cloud
        databases use encryption at rest offered by the platform. On device,
        local data is stored in application-specific storage. You can enhance
        protection by enabling device-level encryption, biometric locks, and a
        strong passcode. We periodically review access permissions for staff and
        contractors, applying least-privilege and revocation procedures. Access
        to operational dashboards is logged and audited. We segment environments
        for development and production and avoid using production Personal Data
        for testing. While we strive for strong security, no system is immune to
        risks; we therefore emphasize minimization, swift patching, and user
        controls such as local deletion and opt-outs to reduce exposure.
      </p>

      {/* 27) Data Accuracy and User Controls */}
      <div className={s.title}>27) Data Accuracy and User Controls</div>
      <p>
        We rely on the information you provide. You can update your email or
        display name through your authentication provider where applicable, and
        those changes will propagate to the App. In-app controls allow you to
        sign out, clear local chat history, toggle dark/light theme, change the
        AI name, and manage notifications. Subscription management is performed
        through your platform account settings, including cancellation and
        refund requests where available. If you notice inaccurate records,
        contact us with details so we can correct them or coordinate with the
        relevant provider. We aim to implement changes promptly, understanding
        that caches and sync cycles can add short delays across devices.
      </p>

      {/* 28) Limitations, Aggregation, and Anonymization */}
      <div className={s.title}>
        28) Limitations, Aggregation, and Anonymization
      </div>
      <p>
        Where possible, we aggregate analytics to reduce identifiability and use
        pseudonymization when full aggregation is not practical. For example, we
        may count paywall impressions, trial starts, or feature usage without
        storing your Chat Content. When data is no longer needed for operational
        purposes, we either delete it or transform it so individuals are not
        reasonably identifiable. Aggregated metrics may be used for product
        planning, capacity management, and reliability monitoring. These
        practices help balance our need to maintain and improve the App with
        your right to privacy. We continuously evaluate whether data elements
        can be shortened, generalized, or removed entirely without impairing
        service quality.
      </p>

      {/* 29) Contact and Responsible Developer */}
      <div className={s.title}>29) Contact and Responsible Developer</div>
      <p>
        For any privacy questions, incident reports, or data subject requests,
        contact{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>. The
        responsible developer and controller is Aleksandra Rakitina. Please
        include relevant context such as your device model, app version, and the
        email used for sign-in so we can verify your identity and respond
        efficiently. Security researchers who believe they have identified a
        vulnerability can responsibly disclose details through this address; we
        appreciate clear reproduction steps and will coordinate timelines
        aligned with user protection. We aim to maintain transparent
        communication and to resolve issues without unnecessary delay while
        safeguarding user data and service integrity.
      </p>
    </PolicyLayout>
  );
}
